import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  updateDoc,
  orderBy,
  limit,
  startAfter
} from 'firebase/firestore';
import { db, auth } from '../../utils/firebase';
import { transformCustomerHair } from '../../api/backendAiService';
import '../../styles/customers.css';
import MetricsRecalculator from './MetricsRecalculator';
import { getSalonStaff, getSalonServices } from '../../api/salonApi';
import {
  calculatePointsLast365Days,
  determineLoyaltyTier,
  getLoyaltyTierSettings
} from '../../utils/updateCustomerMetrics';

function CustomersManager({ salonId, onPreview }) {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [sortBy, setSortBy] = useState('lastVisit');
  const [filterBy, setFilterBy] = useState('all');
  const [showMetricsRecalculator, setShowMetricsRecalculator] = useState(false);
  const [staffList, setStaffList] = useState([]);
  const [servicesList, setServicesList] = useState([]);
  const [notesDraft, setNotesDraft] = useState({});
  const [savingNoteId, setSavingNoteId] = useState(null);
  const [hairTransform, setHairTransform] = useState(null);
  const hairRefInputRef = useRef(null);

  // Load customers on component mount
  useEffect(() => {
    loadCustomers();
  }, [salonId, sortBy, filterBy]);

  // Load staff and services for preference dropdowns
  useEffect(() => {
    const loadStaffAndServices = async () => {
      try {
        const [staff, services] = await Promise.all([
          getSalonStaff(salonId),
          getSalonServices(salonId)
        ]);
        setStaffList(staff.filter(s => s.isActive !== false));
        setServicesList(services.filter(s => s.isActive !== false));
      } catch (err) {
        console.error('Error loading staff/services:', err);
      }
    };
    if (salonId) loadStaffAndServices();
  }, [salonId]);

  // --- Hair Transform Handlers ---

  const SLOT_COUNT = 5;

  const openHairTransform = (customer) => {
    setHairTransform({
      customer,
      referenceFile: null,
      referencePreview: null,
      transformType: 'color',
      loading: false,
      result: null,
      error: null,
      saved: false,
      slots: Array(SLOT_COUNT).fill(null)
    });
  };

  const closeHairTransform = () => setHairTransform(null);

  const handleReferenceImageSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => {
      setHairTransform(prev => ({
        ...prev,
        referenceFile: file,
        referencePreview: reader.result,
        result: null,
        error: null,
        saved: false
      }));
    };
    reader.readAsDataURL(file);
  };

  const handleRunHairTransform = async () => {
    if (!hairTransform?.referenceFile || !hairTransform?.customer?.photoURL) return;
    setHairTransform(prev => ({ ...prev, loading: true, error: null, result: null }));
    try {
      const base64 = hairTransform.referencePreview.split(',')[1];
      const data = await transformCustomerHair({
        salonId,
        customerPhotoUrl: hairTransform.customer.photoURL,
        referenceImageBase64: base64,
        referenceMediaType: hairTransform.referenceFile.type,
        transformType: hairTransform.transformType
      });
      setHairTransform(prev => ({ ...prev, loading: false, result: data }));
    } catch (err) {
      setHairTransform(prev => ({ ...prev, loading: false, error: err.message }));
    }
  };

  const handleSaveTransformedPhoto = async () => {
    if (!hairTransform?.result?.transformedImageUrl || !hairTransform?.customer?.id) return;
    try {
      const customerRef = doc(db, 'customers', hairTransform.customer.id);
      await updateDoc(customerRef, { photoURL: hairTransform.result.transformedImageUrl });
      setCustomers(prev =>
        prev.map(c =>
          c.id === hairTransform.customer.id
            ? { ...c, photoURL: hairTransform.result.transformedImageUrl }
            : c
        )
      );
      setHairTransform(prev => ({ ...prev, saved: true }));
    } catch (err) {
      setHairTransform(prev => ({ ...prev, error: 'Failed to save: ' + err.message }));
    }
  };

  const handleSaveToSlot = () => {
    if (!hairTransform?.result?.transformedImageUrl) return;
    const emptyIndex = hairTransform.slots.findIndex(s => s === null);
    if (emptyIndex === -1) return; // all full
    const newSlots = [...hairTransform.slots];
    newSlots[emptyIndex] = {
      url: hairTransform.result.transformedImageUrl,
      type: hairTransform.transformType,
      description: hairTransform.result.hairDescription || ''
    };
    setHairTransform(prev => ({ ...prev, slots: newSlots }));
  };

  const handleSaveSlotAsProfile = async (index) => {
    const slot = hairTransform?.slots?.[index];
    if (!slot?.url || !hairTransform?.customer?.id) return;
    try {
      const customerRef = doc(db, 'customers', hairTransform.customer.id);
      await updateDoc(customerRef, { photoURL: slot.url });
      setCustomers(prev =>
        prev.map(c =>
          c.id === hairTransform.customer.id ? { ...c, photoURL: slot.url } : c
        )
      );
      setHairTransform(prev => ({ ...prev, saved: true }));
    } catch (err) {
      setHairTransform(prev => ({ ...prev, error: 'Failed to save: ' + err.message }));
    }
  };

  const handleClearSlot = (index) => {
    const newSlots = [...hairTransform.slots];
    newSlots[index] = null;
    setHairTransform(prev => ({ ...prev, slots: newSlots }));
  };

  // --------------------------------

  const loadCustomers = async () => {
    try {
      setLoading(true);
      setError('');

      console.log('Loading customers for salon:', salonId);

      // Get loyalty tier settings for this salon
      const tierSettings = await getLoyaltyTierSettings(salonId);
      console.log('Loaded tier settings:', tierSettings);

      // Query customers collection for this salon
      const customersRef = collection(db, 'customers');
      const q = query(
        customersRef,
        where('salonId', '==', salonId)
      );

      const querySnapshot = await getDocs(q);

      const customersList = [];

      // Process each customer and calculate their loyalty tier
      for (const docSnapshot of querySnapshot.docs) {
        const customerData = docSnapshot.data();

        // Calculate points earned in last 365 days from completed transactions
        const pointsLast365Days = await calculatePointsLast365Days(
          customerData.email,
          salonId
        );

        // For customers with no transaction history (manually created, CSV-uploaded,
        // or seeded via script), pointsLast365Days returns 0 even though the customer
        // may have a meaningful pointsBalance stored in metrics.
        // Fall back to metrics.pointsBalance so the stored value is always respected.
        const effectivePoints = pointsLast365Days > 0
          ? pointsLast365Days
          : (customerData.metrics?.pointsBalance || 0);

        // Determine the loyalty tier based on effective points
        const calculatedTier = determineLoyaltyTier(effectivePoints, tierSettings);

        // Update the customer document if tier has changed
        if (calculatedTier !== customerData.loyaltyTier) {
          console.log(`Updating loyalty tier for ${customerData.email}: ${customerData.loyaltyTier} -> ${calculatedTier} (effectivePoints: ${effectivePoints}, txPoints: ${pointsLast365Days})`);
          const customerRef = doc(db, 'customers', docSnapshot.id);
          await updateDoc(customerRef, {
            loyaltyTier: calculatedTier,
            'metrics.pointsLast365Days': pointsLast365Days,
            updatedAt: new Date()
          });
        }

        customersList.push({
          id: docSnapshot.id,
          ...customerData,
          // Ensure we have required fields with defaults based on your document structure
          displayName: customerData.displayName || 'Unknown Customer',
          email: customerData.email || '',
          phoneNumber: customerData.phoneNumber || '',
          lastVisit: customerData.lastVisit || customerData.lastLoginAt || customerData.createdAt || null,
          totalVisits: customerData.metrics?.totalVisits || 0,
          pointsBalance: customerData.metrics?.pointsBalance || 0,
          totalSpent: customerData.metrics?.totalSpent || 0,
          totalSpentLast365Days: customerData.metrics?.totalSpentLast365Days || 0,
          lastTransaction: customerData.metrics?.lastTransaction || null,
          loyaltyTier: calculatedTier, // Use the calculated tier
          pointsLast365Days: pointsLast365Days, // Include points for display
          createdAt: customerData.createdAt || null,
          birthMonth: customerData.birthMonth || '',
          photoURL: customerData.photoURL || null,
          salonId: customerData.salonId || '',
          isVIP: customerData.isVIP || false, // Add VIP status
          customer_notes: customerData.customer_notes || ''
        });
      }

      console.log(`Loaded ${customersList.length} customers with calculated loyalty tiers`);

      // Apply client-side sorting since Firestore has limitations
      const sortedCustomers = sortCustomers(customersList, sortBy);

      setCustomers(sortedCustomers);

      // Initialise notes draft from loaded data
      const initialNotes = {};
      sortedCustomers.forEach(c => { initialNotes[c.id] = c.customer_notes || ''; });
      setNotesDraft(initialNotes);

      if (customersList.length === 0) {
        setError('No customers found for this salon. Customers will appear here after they register.');
      }

    } catch (err) {
      console.error('Error loading customers:', err);
      setError(`Failed to load customer data: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const sortCustomers = (customersList, sortBy) => {
    return [...customersList].sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return (a.displayName || '').localeCompare(b.displayName || '');
        case 'email':
          return (a.email || '').localeCompare(b.email || '');
        case 'totalVisits':
          return (b.totalVisits || 0) - (a.totalVisits || 0);
        case 'points':
          return (b.pointsBalance || 0) - (a.pointsBalance || 0);
        case 'lastVisit':
        default:
          // Handle Firestore timestamps properly
          const getTimestamp = (dateField) => {
            if (!dateField) return 0;
            if (dateField.seconds) return dateField.seconds * 1000; // Firestore timestamp
            if (dateField.toMillis) return dateField.toMillis(); // Firestore timestamp object
            if (dateField instanceof Date) return dateField.getTime();
            return new Date(dateField).getTime();
          };
          
          const aDate = getTimestamp(a.lastVisit || a.lastLoginAt || a.createdAt);
          const bDate = getTimestamp(b.lastVisit || b.lastLoginAt || b.createdAt);
          return bDate - aDate; // Most recent first
      }
    });
  };

  const filteredCustomers = customers.filter(customer => {
    // Search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch = 
        (customer.displayName || '').toLowerCase().includes(searchLower) ||
        (customer.email || '').toLowerCase().includes(searchLower) ||
        (customer.phoneNumber || '').toLowerCase().includes(searchLower);
      
      if (!matchesSearch) return false;
    }

    // Loyalty filter - VIP uses isVIP field, others use loyaltyTier field
    if (filterBy !== 'all') {
      if (filterBy === 'vip') {
        // VIP filter checks the isVIP boolean field
        if (customer.isVIP !== true) return false;
      } else {
        // Other tiers check the loyaltyTier field
        const customerTier = (customer.loyaltyTier || '').toLowerCase();
        
        if (filterBy === 'platinum' && customerTier !== 'platinum') return false;
        if (filterBy === 'gold' && customerTier !== 'gold') return false;
        if (filterBy === 'silver' && customerTier !== 'silver') return false;
      }
    }

    return true;
  });

  const handleViewCustomerDetails = (customer) => {
    // Navigate to the customer details page
    navigate(`/admin/customers/${salonId}/${customer.id}`, {
      state: { 
        customer: customer
      }
    });
  };

  const handleEditCustomer = (customer) => {
    setEditingCustomer({ ...customer });
  };

  const handleSaveEdit = async () => {
    try {
      if (!editingCustomer || !editingCustomer.id) return;

      const customerRef = doc(db, 'customers', editingCustomer.id);
      
      // Update customer document including VIP status, campaign preferences, and staff/service preferences
      await updateDoc(customerRef, {
        displayName: editingCustomer.displayName,
        phoneNumber: editingCustomer.phoneNumber,
        birthMonth: editingCustomer.birthMonth,
        isVIP: editingCustomer.isVIP || false,
        'preferences.notifications.email': editingCustomer.preferences?.notifications?.email !== false,
        'preferences.notifications.push': editingCustomer.preferences?.notifications?.push !== false,
        'preferences.notifications.whatsapp': editingCustomer.preferences?.notifications?.whatsapp !== false,
        preferredStaff1: editingCustomer.preferredStaff1 || '',
        preferredService1: editingCustomer.preferredService1 || '',
        preferredStaff2: editingCustomer.preferredStaff2 || '',
        preferredService2: editingCustomer.preferredService2 || '',
        updatedAt: new Date()
      });

      // Update local state
      setCustomers(customers.map(customer => 
        customer.id === editingCustomer.id ? editingCustomer : customer
      ));

      setEditingCustomer(null);
      console.log('Customer updated successfully');
      
    } catch (err) {
      console.error('Error updating customer:', err);
      setError(`Failed to update customer: ${err.message}`);
    }
  };

  const handleSaveNotes = async (customerId) => {
    try {
      setSavingNoteId(customerId);
      const noteText = notesDraft[customerId] || '';
      const customerRef = doc(db, 'customers', customerId);
      await updateDoc(customerRef, {
        customer_notes: noteText,
        updatedAt: new Date()
      });
      // Reflect in local customer list
      setCustomers(prev =>
        prev.map(c => c.id === customerId ? { ...c, customer_notes: noteText } : c)
      );
    } catch (err) {
      console.error('Error saving customer notes:', err);
      setError(`Failed to save notes: ${err.message}`);
    } finally {
      setSavingNoteId(null);
    }
  };

  const formatDate = (date) => {
    if (!date) return 'Never';
    
    try {
      let dateObj;
      
      // Handle Firestore timestamp object
      if (date.seconds) {
        dateObj = new Date(date.seconds * 1000);
      } else if (date.toDate && typeof date.toDate === 'function') {
        dateObj = date.toDate();
      } else if (date instanceof Date) {
        dateObj = date;
      } else {
        dateObj = new Date(date);
      }
      
      return dateObj.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (err) {
      console.error('Error formatting date:', err, date);
      return 'Invalid Date';
    }
  };

  const getLoyaltyBadgeClass = (tier) => {
    switch (tier?.toLowerCase()) {
      case 'vip': return 'loyalty-badge vip';
      case 'platinum': return 'loyalty-badge platinum';
      case 'gold': return 'loyalty-badge gold';
      case 'silver': return 'loyalty-badge silver';
      case 'bronze': 
      default: return 'loyalty-badge bronze';
    }
  };

  if (loading) {
    return (
      <div className="customers-manager">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading customer data...</p>
        </div>
      </div>
    );
  }

  // Show metrics recalculator if toggled
  if (showMetricsRecalculator) {
    return (
      <div className="customers-manager">
        <div className="customers-header">
          <h2>Customers</h2>
          <button onClick={() => setShowMetricsRecalculator(false)} className="refresh-button">
            ← Back to Customers
          </button>
        </div>
        <MetricsRecalculator salonId={salonId} />
      </div>
    );
  }

  return (
    <div className="customers-manager">
      <div className="editor-page-header">
        <h2>Customers</h2>
        <div className="editor-page-actions">
          <button onClick={() => setShowMetricsRecalculator(true)} className="refresh-button" style={{ background: '#ff9800' }}>
            🔄 Recalculate Metrics
          </button>
          <button onClick={loadCustomers} className="refresh-button">
            Refresh Data
          </button>
          <button className="page-action-btn page-action-preview" type="button" onClick={onPreview}>Live Preview</button>
        </div>
      </div>

      {error && (
        <div className="error-banner">
          <p>{error}</p>
          <button onClick={loadCustomers} className="retry-button">
            Try Again
          </button>
        </div>
      )}

      <div className="customers-controls">
        <div className="search-section">
          <input
            type="text"
            placeholder="Search customers by name, email, or phone..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>

        <div className="filter-section">
          <select 
            value={sortBy} 
            onChange={(e) => setSortBy(e.target.value)}
            className="sort-select"
          >
            <option value="lastVisit">Sort by Last Visit</option>
            <option value="name">Sort by Name</option>
            <option value="email">Sort by Email</option>
            <option value="totalVisits">Sort by Total Visits</option>
            <option value="points">Sort by Points</option>
          </select>

          <select 
            value={filterBy} 
            onChange={(e) => setFilterBy(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Customers</option>
            <option value="vip">VIP Members</option>
            <option value="platinum">Platinum Members</option>
            <option value="gold">Gold Members</option>
            <option value="silver">Silver Members</option>
          </select>
        </div>
      </div>

      <div className="customers-stats">
        <div className="stat-card">
          <div className="stat-number">{customers.length}</div>
          <div className="stat-label">Total Customers</div>
        </div>
        <div className="stat-card">
          <div className="stat-number">
            {customers.filter(c => c.isVIP === true).length}
          </div>
          <div className="stat-label">VIP Members</div>
        </div>
        <div className="stat-card">
          <div className="stat-number">
            {Math.round(customers.reduce((sum, c) => sum + (c.totalVisits || 0), 0) / Math.max(customers.length, 1))}
          </div>
          <div className="stat-label">Avg. Visits</div>
        </div>
      </div>

      <div className="customers-table-container">
        <table className="customers-table">
          <thead>
            <tr>
              <th>Customer</th>
              <th>Customer Notes</th>
              <th>Loyalty Tier</th>
              <th>Customer Details</th>
            </tr>
          </thead>
          <tbody>
            {filteredCustomers.length > 0 ? (
              filteredCustomers.map((customer) => (
                <tr key={customer.id}>
                  <td>
                    <div className="customer-info">
                      <div className="customer-avatar">
                        {customer.photoURL ? (
                          <>
                            <img src={customer.photoURL} alt={customer.displayName} />
                            {(salonId === 'demo-salon' || salonId === 'the-scene') && (
                              <div
                                className="hair-transform-overlay"
                                onClick={() => openHairTransform(customer)}
                                title="AI Hair Transform"
                              >
                                <span>✂</span>
                              </div>
                            )}
                          </>
                        ) : (
                          <div className="avatar-placeholder">
                            {(customer.displayName || 'U').charAt(0).toUpperCase()}
                          </div>
                        )}
                      </div>
                      <div className="customer-details">
                        <div className="customer-name">
                          {customer.displayName || 'Unknown'}
                          {customer.isVIP && <span className="vip-indicator"> ⭐ VIP</span>}
                        </div>
                        <div className="customer-contact-inline">
                          <span className="contact-email">{customer.email || 'No email'}</span>
                          {customer.phoneNumber && (
                            <span className="contact-phone">{customer.phoneNumber}</span>
                          )}
                        </div>
                        <div className="customer-since">
                          Member since {formatDate(customer.createdAt)}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <div className="customer-notes-cell">
                      <textarea
                        className="customer-notes-textarea"
                        value={notesDraft[customer.id] !== undefined ? notesDraft[customer.id] : (customer.customer_notes || '')}
                        onChange={(e) => setNotesDraft(prev => ({ ...prev, [customer.id]: e.target.value }))}
                        placeholder="Add notes about this customer..."
                        rows={3}
                      />
                      <button
                        className="notes-save-button"
                        onClick={() => handleSaveNotes(customer.id)}
                        disabled={savingNoteId === customer.id}
                      >
                        {savingNoteId === customer.id ? 'Saving...' : 'Save'}
                      </button>
                    </div>
                  </td>
                  <td>
                    <span className={getLoyaltyBadgeClass(customer.loyaltyTier)}>
                      {customer.loyaltyTier || 'Bronze'}
                    </span>
                  </td>
                  <td>
                    <div className="customer-metrics">
                      <div className="metric-item">
                        <span className="metric-label">Total Spend:</span>
                        <span className="metric-value">${(customer.totalSpent || 0).toFixed(2)}</span>
                      </div>
                      <div className="metric-item">
                        <span className="metric-label">Total Spend (Last 365 Days):</span>
                        <span className="metric-value">${(customer.totalSpentLast365Days || 0).toFixed(2)}</span>
                      </div>
                      <div className="metric-item">
                        <span className="metric-label">Points Balance:</span>
                        <span className="metric-value">{customer.pointsBalance || 0}</span>
                      </div>
                      <div className="metric-item">
                        <span className="metric-label">Last Transaction:</span>
                        <span className="metric-value">{formatDate(customer.lastTransaction)}</span>
                      </div>
                      <div className="metric-item">
                        <span className="metric-label">Actions:</span>
                        <div className="action-buttons-inline">
                          <button
                            onClick={() => handleViewCustomerDetails(customer)}
                            className="view-button"
                          >
                            View
                          </button>
                          <button
                            onClick={() => handleEditCustomer(customer)}
                            className="edit-button"
                          >
                            Edit
                          </button>
                        </div>
                      </div>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="4" className="no-customers">
                  {searchTerm || filterBy !== 'all'
                    ? 'No customers match your search criteria.'
                    : 'No customers found. They will appear here after registering.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Customer Detail Modal - Keep existing simple modal for backward compatibility */}
      {selectedCustomer && (
        <div className="modal-overlay" onClick={() => setSelectedCustomer(null)}>
          <div className="customer-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Customer Details</h3>
              <button onClick={() => setSelectedCustomer(null)} className="close-button">×</button>
            </div>
            <div className="modal-content">
              <div className="customer-profile">
                <div className="profile-section">
                  <h4>Personal Information</h4>
                  <p><strong>Name:</strong> {selectedCustomer.displayName}</p>
                  <p><strong>Email:</strong> {selectedCustomer.email}</p>
                  <p><strong>Phone:</strong> {selectedCustomer.phoneNumber || 'Not provided'}</p>
                  <p><strong>Birth Month:</strong> {selectedCustomer.birthMonth || 'Not provided'}</p>
                  <p><strong>VIP Status:</strong> {selectedCustomer.isVIP ? 'Yes ⭐' : 'No'}</p>
                </div>
                
                <div className="profile-section">
                  <h4>Loyalty Information</h4>
                  <p><strong>Tier:</strong> {selectedCustomer.loyaltyTier}</p>
                  <p><strong>Points Balance:</strong> {selectedCustomer.pointsBalance}</p>
                  <p><strong>Total Spend:</strong> ${(selectedCustomer.totalSpent || 0).toFixed(2)}</p>
                  <p><strong>Total Spend (Last 365 Days):</strong> ${(selectedCustomer.totalSpentLast365Days || 0).toFixed(2)}</p>
                  <p><strong>Member Since:</strong> {formatDate(selectedCustomer.createdAt)}</p>
                  <p><strong>Last Transaction:</strong> {formatDate(selectedCustomer.lastTransaction)}</p>
                </div>

                <div className="profile-section">
                  <h4>Preferences</h4>
                  <p><strong>Email Notifications:</strong> {selectedCustomer.preferences?.notifications?.email ? 'Enabled' : 'Disabled'}</p>
                  <p><strong>Push Notifications:</strong> {selectedCustomer.preferences?.notifications?.push ? 'Enabled' : 'Disabled'}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Hair Transform Modal */}
      {hairTransform && (
        <div className="modal-overlay" onClick={closeHairTransform}>
          <div className="hair-transform-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>AI Hair Transformation — {hairTransform.customer?.displayName}</h3>
              <button onClick={closeHairTransform} className="close-button">×</button>
            </div>

            <div className="hair-transform-body">
              {/* Left: inputs */}
              <div className="ht-left-panel">
                <div className="ht-photo-row">
                  <div className="ht-photo-block">
                    <p className="ht-label">Customer Photo</p>
                    <div className="ht-photo-preview">
                      <img src={hairTransform.customer.photoURL} alt="Customer" />
                    </div>
                  </div>

                  <div className="ht-photo-block">
                    <p className="ht-label">Reference Image</p>
                    <div
                      className={`ht-reference-zone ${hairTransform.referencePreview ? 'has-image' : ''}`}
                      onClick={() => hairRefInputRef.current?.click()}
                    >
                      {hairTransform.referencePreview ? (
                        <img src={hairTransform.referencePreview} alt="Reference hair" />
                      ) : (
                        <div className="ht-upload-prompt">
                          <span className="ht-upload-icon">📷</span>
                          <span>Upload hair reference</span>
                          <span className="ht-upload-hint">JPG or PNG</span>
                        </div>
                      )}
                    </div>
                    <input
                      ref={hairRefInputRef}
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      onChange={handleReferenceImageSelect}
                      style={{ display: 'none' }}
                    />
                    {hairTransform.referencePreview && (
                      <button
                        className="ht-change-ref-btn"
                        onClick={() => hairRefInputRef.current?.click()}
                      >
                        Change Image
                      </button>
                    )}
                  </div>
                </div>

                <div className="ht-type-selector">
                  <p className="ht-label">What to Transform</p>
                  <div className="ht-type-options">
                    <button
                      className={`ht-type-btn ${hairTransform.transformType === 'color' ? 'active' : ''}`}
                      onClick={() => setHairTransform(prev => ({ ...prev, transformType: 'color', result: null, saved: false }))}
                    >
                      Hair Color
                    </button>
                    <button
                      className={`ht-type-btn ${hairTransform.transformType === 'style' ? 'active' : ''}`}
                      onClick={() => setHairTransform(prev => ({ ...prev, transformType: 'style', result: null, saved: false }))}
                    >
                      Hair Style
                    </button>
                  </div>
                </div>

                <button
                  className="ht-transform-btn"
                  onClick={handleRunHairTransform}
                  disabled={!hairTransform.referenceFile || hairTransform.loading}
                >
                  {hairTransform.loading ? 'Transforming…' : 'Transform Hair'}
                </button>
              </div>

              {/* Right: result */}
              <div className="ht-right-panel">
                <p className="ht-label">Result</p>
                {hairTransform.loading ? (
                  <div className="ht-loading">
                    <div className="loading-spinner"></div>
                    <p>AI is transforming the hair…</p>
                    <p className="ht-loading-note">This may take 30–60 seconds</p>
                  </div>
                ) : hairTransform.result ? (
                  <div className="ht-result">
                    <div className="ht-result-preview">
                      <img src={hairTransform.result.transformedImageUrl} alt="Transformed" />
                    </div>
                    {hairTransform.result.hairDescription && (
                      <p className="ht-detected-label">
                        Detected: <em>{hairTransform.result.hairDescription}</em>
                      </p>
                    )}
                    <div className="ht-result-actions">
                      <button
                        className="ht-slot-save-btn"
                        onClick={handleSaveToSlot}
                        disabled={hairTransform.slots.every(s => s !== null)}
                        title={hairTransform.slots.every(s => s !== null) ? 'All 5 comparison slots are full' : 'Save to comparison strip'}
                      >
                        {hairTransform.slots.every(s => s !== null) ? 'Slots Full' : 'Save to Comparison'}
                      </button>
                      {hairTransform.saved ? (
                        <p className="ht-saved-notice">✓ Set as profile photo</p>
                      ) : (
                        <button className="ht-save-btn" onClick={handleSaveTransformedPhoto}>
                          Set as Profile Photo
                        </button>
                      )}
                    </div>
                  </div>
                ) : hairTransform.error ? (
                  <div className="ht-error">
                    <p><strong>Transform failed</strong></p>
                    <p>{hairTransform.error}</p>
                  </div>
                ) : (
                  <div className="ht-placeholder">
                    <p>Upload a reference image and click <strong>Transform Hair</strong> to preview the result here.</p>
                  </div>
                )}
              </div>
            </div>

            {/* Comparison Slots Strip */}
            <div className="ht-slots-section">
              <p className="ht-slots-label">
                Comparison Slots
                <span className="ht-slots-hint">
                  — save up to 5 results here to compare · cleared when you close the modal
                </span>
              </p>
              <div className="ht-slots-row">
                {hairTransform.slots.map((slot, i) => (
                  <div key={i} className={`ht-slot ${slot ? 'ht-slot--filled' : 'ht-slot--empty'}`}>
                    {slot ? (
                      <>
                        <img src={slot.url} alt={`Comparison ${i + 1}`} className="ht-slot-img" />
                        <span className={`ht-slot-badge ht-slot-badge--${slot.type}`}>
                          {slot.type === 'color' ? 'Colour' : 'Style'}
                        </span>
                        <div className="ht-slot-overlay">
                          <button
                            className="ht-slot-action-btn ht-slot-profile-btn"
                            onClick={() => handleSaveSlotAsProfile(i)}
                          >
                            Set as Profile
                          </button>
                          <button
                            className="ht-slot-action-btn ht-slot-remove-btn"
                            onClick={() => handleClearSlot(i)}
                          >
                            ✕ Remove
                          </button>
                        </div>
                      </>
                    ) : (
                      <div className="ht-slot-empty-inner">
                        <span className="ht-slot-number">{i + 1}</span>
                        <span className="ht-slot-empty-label">Empty</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

          </div>
        </div>
      )}

      {/* Edit Customer Modal */}
      {editingCustomer && (
        <div className="modal-overlay" onClick={() => setEditingCustomer(null)}>
          <div className="customer-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Edit Customer</h3>
              <button onClick={() => setEditingCustomer(null)} className="close-button">×</button>
            </div>
            <div className="modal-content">
              <form onSubmit={(e) => { e.preventDefault(); handleSaveEdit(); }}>
                <div className="form-group">
                  <label>Display Name</label>
                  <input
                    type="text"
                    value={editingCustomer.displayName || ''}
                    onChange={(e) => setEditingCustomer({
                      ...editingCustomer,
                      displayName: e.target.value
                    })}
                  />
                </div>
                
                <div className="form-group">
                  <label>Phone Number</label>
                  <input
                    type="tel"
                    value={editingCustomer.phoneNumber || ''}
                    onChange={(e) => setEditingCustomer({
                      ...editingCustomer,
                      phoneNumber: e.target.value
                    })}
                  />
                </div>
                
                <div className="form-group">
                  <label>Birth Month</label>
                  <select
                    value={editingCustomer.birthMonth || ''}
                    onChange={(e) => setEditingCustomer({
                      ...editingCustomer,
                      birthMonth: e.target.value
                    })}
                  >
                    <option value="">Select Month</option>
                    <option value="January">January</option>
                    <option value="February">February</option>
                    <option value="March">March</option>
                    <option value="April">April</option>
                    <option value="May">May</option>
                    <option value="June">June</option>
                    <option value="July">July</option>
                    <option value="August">August</option>
                    <option value="September">September</option>
                    <option value="October">October</option>
                    <option value="November">November</option>
                    <option value="December">December</option>
                  </select>
                </div>

                <div className="form-group checkbox-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={editingCustomer.isVIP || false}
                      onChange={(e) => setEditingCustomer({
                        ...editingCustomer,
                        isVIP: e.target.checked
                      })}
                    />
                    <span className="checkbox-text">Mark as VIP Customer</span>
                  </label>
                  <p className="checkbox-description">
                    VIP customers receive special recognition and may be eligible for exclusive benefits.
                  </p>
                </div>

                <div className="form-section-divider">
                  <h4>Campaign Subscription Preferences</h4>
                </div>

                <div className="form-group checkbox-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={editingCustomer.preferences?.notifications?.push !== false}
                      onChange={(e) => setEditingCustomer({
                        ...editingCustomer,
                        preferences: {
                          ...editingCustomer.preferences,
                          notifications: {
                            ...editingCustomer.preferences?.notifications,
                            push: e.target.checked
                          }
                        }
                      })}
                    />
                    <span className="checkbox-text">Push Notification Campaigns</span>
                  </label>
                </div>

                <div className="form-group checkbox-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={editingCustomer.preferences?.notifications?.email !== false}
                      onChange={(e) => setEditingCustomer({
                        ...editingCustomer,
                        preferences: {
                          ...editingCustomer.preferences,
                          notifications: {
                            ...editingCustomer.preferences?.notifications,
                            email: e.target.checked
                          }
                        }
                      })}
                    />
                    <span className="checkbox-text">Email Campaigns</span>
                  </label>
                </div>

                <div className="form-group checkbox-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={editingCustomer.preferences?.notifications?.whatsapp !== false}
                      onChange={(e) => setEditingCustomer({
                        ...editingCustomer,
                        preferences: {
                          ...editingCustomer.preferences,
                          notifications: {
                            ...editingCustomer.preferences?.notifications,
                            whatsapp: e.target.checked
                          }
                        }
                      })}
                    />
                    <span className="checkbox-text">WhatsApp Campaigns</span>
                  </label>
                </div>

                <div className="form-section-divider">
                  <h4>Customer Preferences</h4>
                </div>

                <div className="form-group">
                  <label>Preferred Staff 1</label>
                  <select
                    value={editingCustomer.preferredStaff1 || ''}
                    onChange={(e) => setEditingCustomer({
                      ...editingCustomer,
                      preferredStaff1: e.target.value
                    })}
                  >
                    <option value="">-- Select Staff --</option>
                    {staffList.map(staff => (
                      <option key={staff.id} value={staff.name}>{staff.name}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Preferred Service 1</label>
                  <select
                    value={editingCustomer.preferredService1 || ''}
                    onChange={(e) => setEditingCustomer({
                      ...editingCustomer,
                      preferredService1: e.target.value
                    })}
                  >
                    <option value="">-- Select Service --</option>
                    {servicesList.map(service => (
                      <option key={service.id} value={service.name}>{service.name}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Preferred Staff 2</label>
                  <select
                    value={editingCustomer.preferredStaff2 || ''}
                    onChange={(e) => setEditingCustomer({
                      ...editingCustomer,
                      preferredStaff2: e.target.value
                    })}
                  >
                    <option value="">-- Select Staff --</option>
                    {staffList.map(staff => (
                      <option key={staff.id} value={staff.name}>{staff.name}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Preferred Service 2</label>
                  <select
                    value={editingCustomer.preferredService2 || ''}
                    onChange={(e) => setEditingCustomer({
                      ...editingCustomer,
                      preferredService2: e.target.value
                    })}
                  >
                    <option value="">-- Select Service --</option>
                    {servicesList.map(service => (
                      <option key={service.id} value={service.name}>{service.name}</option>
                    ))}
                  </select>
                </div>

                <div className="form-actions">
                  <button type="button" onClick={() => setEditingCustomer(null)} className="cancel-button">
                    Cancel
                  </button>
                  <button type="submit" className="save-button">
                    Save Changes
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CustomersManager;